<template>
    <div class="rbc-container">
        <svg
            :width="width"
            :height="height"
            overflow="visible"
        >
            <rect
                v-for="(r, i) in rects"
                :key="i"
                v-bind="r"
                shape-rendering="crispEdges"
                @mouseover="onMouseOver(r)"
                @mouseout="onMouseOut(r)"
            />
            <rect
                v-for="(r, i) in displayRects"
                :key="`display-${i}`"
                v-bind="r"
                shape-rendering="crispEdges"
                :class="`display ${r.value.type}`"
                @mouseover="onMouseOver(r)"
                @mouseout="onMouseOut(r)"
            />
            <!-- <line
                v-for="({x, value, height: lineHeight}, i) in labels"
                :key="`line-${i}`"
                class="compare-line"
                :x1="x"
                :x2="x"
                :y1="height - lineHeight"
                :y2="height"
            /> -->
            <text
                v-for="({x, value}, i) in labels"
                ref="label"
                :key="`label-${i}`"
                class="compare-label"
                :x="x"
                :y="height + 10"
            >
                <slot
                    name="label-content"
                    :value="value"
                />
            </text>
        </svg>
        <div
            v-if="hoverRect"
            ref="tooltip"
            class="tooltip-content"
            :style="`left:${hoverRect.x + 5}px`"
        >
            <slot
                name="tooltip-content"
                :value="hoverRect.value"
            />
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { setTransformToPreventOverflow } from './helpers';

const { scaleLinear, scaleBand } = d3;

export default {
    props: {
        values: {
            type: Array,
            default: () => []
        },
        valueProp: {
            type: String,
            default: ''
        },
        width: {
            type: Number,
            default: 100
        },
        height: {
            type: Number,
            default: 40
        },
        expandHighlightedFactor: {
            type: Number,
            default: 1.5
        },
        labelValues: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            hoverRect: null
        };
    },
    computed: {
        valueAccessor() {
            if (this.valueProp) {
                return el => el[this.valueProp];
            }
            return el => el;
        },
        xScale() {
            return scaleBand().padding(0).domain(this.values.map((v, i) => i)).range([0, this.width]);
        },
        yScale() {
            return scaleLinear().domain([0, Math.max(...this.values.map(this.valueAccessor))]).range([0, this.height]);
        },
        rects() {
            return _.map(this.values, (value, i) => ({
                x: this.xScale(i),
                y: this.height - this.yScale(this.valueAccessor(value)),

                width: this.xScale.bandwidth(),
                height: this.yScale(this.valueAccessor(value)),

                value
            }));
        },
        displayRects() {
            return this.rects
                .filter(({ value: { type } }) => type !== 'none')
                .map(({ x, width, ...rest }) => {
                    const type = rest.value.type;
                    if (type === 'value') {
                        return {
                            x: x - (width * (this.expandHighlightedFactor - 1) / 2),
                            width: width * this.expandHighlightedFactor,
                            ...rest
                        };
                    }
                    return {
                        x,
                        width: 2,
                        ...rest
                    };
                });
        },
        labels() {
            return _.map(this.labelValues, value => {
                const i = _.findIndex(this.values, value);
                return {
                    x: this.xScale(i),
                    height: this.yScale(this.valueAccessor(value)),
                    value
                };
            });
        }
    },
    mounted() {
        this.fixLabelPositions();
    },
    async updated() {
        await this.$nextTick();
        this.fixLabelPositions();
        if (this.hoverRect) {
            this.fixTooltipPosition();
        }
    },
    methods: {
        onMouseOver(rect) {
            this.hoverRect = rect;
            this.$emit('hover', rect);
        },
        onMouseOut() {
            this.hoverRect = null;
            this.$emit('hover', null);
        },
        fixLabelPositions() {
            this.$refs.label.forEach(setTransformToPreventOverflow);
        },
        fixTooltipPosition() {
            const right = this.$refs.tooltip.getBoundingClientRect().right;
            if (right > window.innerWidth) {
                // Adjust tooltip to be on screen with 5px padding
                const adjustment = (right - window.innerWidth) + 25;
                this.$refs.tooltip.style.transform = `translateX(-${adjustment}px)`;
            }
        }
    }
};
</script>

<style src="./common.chart.css" scoped></style>
<style scoped lang="postcss">
.rbc-container {
    @apply relative;
}

svg {
    overflow: visible !important;
}

rect {
    @apply fill-current;
}

rect:hover {
    @apply stroke-current opacity-50;
    stroke-width: 1px;
}

rect:not(.value) {
    @apply text-neutral-4;
}

rect.comparison {
    @apply text-neutral-text
}

text.compare-label {
    @apply fill-current text-neutral-text;
}

line.compare-line {
    @apply stroke-current text-neutral-2 pointer-events-none;
    stroke-width: 2px;
}
</style>
